import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { User, Search, MapPin, Calendar, Download, UserPlus } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';
import { downloadUserVCard, saveContactToBackend } from '../lib/contactDownload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrowseUsers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    const tab = searchParams.get('tab') || 'all';
    setActiveTab(tab);
    
    if (tab === 'nearby') {
      loadNearbyUsers();
    } else {
      loadAllUsers();
    }
  }, [searchParams]);

  const loadAllUsers = async () => {
    try {
      const headers = getAuthHeaders();
      console.log('Loading all users with headers:', headers);
      
      const response = await axios.get(`${API}/users/all`, { headers });
      console.log('Users response:', response.data);
      
      setUsers(response.data.users || []);
      
      if (!response.data.users || response.data.users.length === 0) {
        toast.info('No users found yet');
      }
    } catch (error) {
      console.error('Load users error:', error.response?.data || error.message);
      
      if (error.response?.status === 401) {
        toast.error('Please login to browse users');
        navigate('/auth');
      } else {
        toast.error(`Failed to load users: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadNearbyUsers = async () => {
    try {
      // Get user's current location
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser');
        toast.error('Geolocation not supported');
        setLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          
          try {
            const response = await axios.get(`${API}/nearby`, {
              params: { lat: latitude, lng: longitude }
            });
            setUsers(response.data.users || []);
            if (response.data.users.length === 0) {
              toast.info('No nearby users found. Try browsing all users.');
            }
          } catch (error) {
            console.error('Load nearby users error:', error);
            toast.error('Failed to load nearby users');
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationError('Unable to get your location. Please enable location access.');
          toast.error('Location access denied. Showing all users instead.');
          loadAllUsers();
        }
      );
    } catch (error) {
      console.error('Nearby users error:', error);
      toast.error('Failed to load nearby users');
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.businessName?.toLowerCase().includes(query) ||
      user.businessCategory?.toLowerCase().includes(query) ||
      user.address?.toLowerCase().includes(query) ||
      user.zipcode?.includes(query)
    );
  });

  const handleViewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const handleScheduleMeeting = (userId) => {
    navigate(`/schedule/${userId}`);
  };

  const handleDownloadVCard = async (user) => {
    try {
      await downloadUserVCard(user.id, activeTab === 'nearby' ? 'nearby' : 'browse');
      toast.success(`Downloaded ${user.name}'s contact!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download contact. Please try again.');
    }
  };

  const handleSaveToContacts = async (user) => {
    try {
      const contactData = {
        name: user.name || '',
        businessName: user.businessName || '',
        title: user.businessCategory || '',
        email: user.email || '',
        phone: user.phone || '',
        mobile: user.mobile || user.phone || '',
        website: user.website || '',
        address: user.address || '',
        linkedin: user.linkedin || '',
        notes: `Saved from ${activeTab === 'nearby' ? 'Nearby Users' : 'Browse Users'}`,
      };

      const result = await saveContactToBackend(contactData, activeTab === 'nearby' ? 'nearby' : 'browse');
      
      if (result.success) {
        toast.success(`✅ ${user.name} saved to your contacts!`);
      } else {
        toast.error(result.message || 'Failed to save contact');
      }
    } catch (error) {
      console.error('Save contact error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please login to save contacts');
      } else {
        toast.error('Failed to save contact. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {activeTab === 'nearby' ? 'Nearby Users' : 'Browse All Users'}
          </h1>
          <p className="text-slate-600 mb-6">
            {activeTab === 'nearby' 
              ? 'Discover professionals near your location' 
              : 'Discover and connect with professionals'}
          </p>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              onClick={() => {
                setActiveTab('all');
                navigate('/browse?tab=all');
              }}
              variant={activeTab === 'all' ? 'default' : 'outline'}
              className={activeTab === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              <User className="w-4 h-4 mr-2" />
              All Users
            </Button>
            <Button
              onClick={() => {
                setActiveTab('nearby');
                navigate('/browse?tab=nearby');
              }}
              variant={activeTab === 'nearby' ? 'default' : 'outline'}
              className={activeTab === 'nearby' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Nearby
            </Button>
          </div>

          {/* Location Error Message */}
          {locationError && activeTab === 'nearby' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 text-sm">{locationError}</p>
            </div>
          )}
          
          {/* Search Bar */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by name, business, category, location, or zipcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-6 text-lg"
            />
          </div>
        </div>

        {/* Users Grid */}
        {filteredUsers.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">
              {searchQuery ? 'No users found matching your search' : 'No users available yet'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user, idx) => (
              <Card key={user.id || idx} className="p-6 bg-white hover:shadow-xl transition-shadow" data-testid={`user-card-${idx}`}>
                <div className="flex flex-col items-center text-center space-y-4">
                  {/* Avatar */}
                  <div
                    onClick={() => handleViewProfile(user.id)}
                    className="cursor-pointer"
                    data-testid={`user-avatar-${idx}`}
                  >
                    {user.profilePicUrl ? (
                      <Avatar className="w-20 h-20">
                        <AvatarImage src={user.profilePicUrl} />
                        <AvatarFallback>{user.name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-10 h-10 text-slate-500" />
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="w-full">
                    <h3
                      onClick={() => handleViewProfile(user.id)}
                      className="text-xl font-bold text-slate-900 mb-1 cursor-pointer hover:text-blue-600"
                      data-testid={`user-name-${idx}`}
                    >
                      {user.name}
                    </h3>
                    {user.businessName && (
                      <p className="text-sm font-medium text-slate-600 mb-2">{user.businessName}</p>
                    )}
                    {user.businessCategory && (
                      <p className="text-sm text-slate-500 mb-2">{user.businessCategory}</p>
                    )}
                    {(user.address || user.zipcode) && (
                      <div className="flex items-center justify-center gap-1 text-xs text-slate-500 mb-3">
                        <MapPin className="w-3 h-3" />
                        <span>{user.zipcode ? `ZIP ${user.zipcode}` : user.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="w-full space-y-2">
                    <Button
                      onClick={() => handleViewProfile(user.id)}
                      variant="outline"
                      className="w-full"
                      data-testid={`view-profile-btn-${idx}`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                    <Button
                      onClick={() => handleScheduleMeeting(user.id)}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid={`schedule-btn-${idx}`}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Meeting
                    </Button>
                    <Button
                      onClick={() => handleSaveToContacts(user)}
                      variant="outline"
                      className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                      data-testid={`save-contact-btn-${idx}`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Save to Contacts
                    </Button>
                    <Button
                      onClick={() => handleDownloadVCard(user)}
                      variant="outline"
                      className="w-full border-green-200 text-green-700 hover:bg-green-50"
                      data-testid={`download-vcard-btn-${idx}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download vCard
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseUsers;
